/**
 * instagram-feed.js — fills the Instagram band from a Behold.so feed.
 *
 * Behold proxies the Instagram Graph API and republishes it as a plain public
 * JSON document, no key and no token. The Next.js storefront fetched the same
 * URL on the server (src/lib/behold.ts); Liquid has no fetch, so it happens in
 * the browser instead. The endpoint sends permissive CORS headers — verified
 * against this storefront's origin, not assumed.
 *
 * The section renders hidden and is only revealed once posts actually arrive,
 * which reproduces the source's `if (posts.length === 0) return null`: a dead
 * feed leaves no empty heading behind. With JavaScript off the band is simply
 * absent, which is the same outcome.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-instagram-feed]';

  function tile(post, iconMarkup) {
    var sizes = post.sizes || {};
    var medium = sizes.medium || sizes.small || sizes.large || sizes.full || {};
    var src = medium.mediaUrl || post.thumbnailUrl || post.mediaUrl;
    if (!src) return null;

    var link = document.createElement('a');
    link.className = 'instagram-feed__item';
    link.href = post.permalink || '#';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    var img = document.createElement('img');
    img.className = 'instagram-feed__image';
    img.src = src;
    img.loading = 'lazy';
    img.decoding = 'async';
    if (medium.width) img.width = medium.width;
    if (medium.height) img.height = medium.height;
    // A caption is a sentence, not a description of the picture. Naming the
    // network is more use to a screen reader than 200 characters of hashtags.
    img.alt = post.prunedCaption || post.caption || 'Instagram post';

    var overlay = document.createElement('span');
    overlay.className = 'instagram-feed__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = iconMarkup;

    link.appendChild(img);
    link.appendChild(overlay);
    return link;
  }

  function render(root) {
    var url = root.getAttribute('data-feed-url');
    var grid = root.querySelector('[data-instagram-grid]');
    var iconEl = root.querySelector('[data-instagram-icon]');
    if (!url || !grid) return;

    var limit = parseInt(root.getAttribute('data-count'), 10) || 6;
    var iconMarkup = iconEl ? iconEl.innerHTML : '';

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('feed responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var posts = (data && data.posts) || [];
        var fragment = document.createDocumentFragment();
        var added = 0;

        for (var i = 0; i < posts.length && added < limit; i++) {
          var node = tile(posts[i], iconMarkup);
          if (node) {
            fragment.appendChild(node);
            added++;
          }
        }

        if (added === 0) return;
        grid.appendChild(fragment);
        root.hidden = false;
      })
      .catch(function () {
        // Instagram is decoration. A feed that is down, rate-limited or
        // reconfigured must never take the page with it, so this is silent by
        // design — the band stays hidden and everything below it is unaffected.
      });
  }

  function init() {
    var roots = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) render(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor tears a section down and rebuilds it on every edit.
  document.addEventListener('shopify:section:load', function (event) {
    var roots = event.target.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) render(roots[i]);
  });
})();
